import { useState } from 'react';
import { Link as RouterLink } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Alert, Box, Stack, Typography } from '@mui/material';
import { colorTokens } from '../app/designTokens';
import { PublicOnboardingShell } from '../components/PublicOnboardingShell';
import { Button } from '../components/ui/Button';
import { TextInput } from '../components/ui/TextInput';
import { forgotPassword } from '../api/authApi';

const { semantic } = colorTokens;

export function ForgotPasswordPage() {
  const { t } = useTranslation(['auth', 'common']);
  const [email, setEmail] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);
  const [submitted, setSubmitted] = useState(false);

  const handleSubmit = async (event) => {
    event.preventDefault();
    setError(null);
    setSubmitting(true);

    try {
      await forgotPassword(email.trim());
      setSubmitted(true);
    } catch (_err) {
      // Keep UX non-enumerating even on transport errors after a successful-shaped API.
      setSubmitted(true);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <PublicOnboardingShell
      maxWidth="sm"
      title={t('auth:forgot_password_title')}
      subtitle={submitted ? undefined : t('auth:forgot_password_subtitle')}
      footer={(
        <Typography component="span" sx={{ fontSize: 12, color: semantic.text.muted }}>
          <Box
            component={RouterLink}
            to="/login"
            sx={{ color: semantic.text.secondary, textDecoration: 'underline' }}
          >
            {t('auth:back_to_login')}
          </Box>
        </Typography>
      )}
      testId="public-forgot-password-shell"
    >
      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      {submitted ? (
        <Alert severity="success" data-testid="forgot-password-success">
          {t('auth:forgot_password_success')}
        </Alert>
      ) : (
        <Box component="form" onSubmit={handleSubmit}>
          <Stack spacing={2}>
            <TextInput
              label={t('auth:email')}
              type="email"
              required
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              autoComplete="email"
              inputProps={{ 'data-testid': 'forgot-password-email' }}
            />
            <Button
              type="submit"
              variant="Primary"
              fullWidth
              disabled={submitting || !email.trim()}
              loading={submitting}
            >
              {t('auth:forgot_password_submit')}
            </Button>
          </Stack>
        </Box>
      )}
    </PublicOnboardingShell>
  );
}
